import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, type Catalog, type ServerView } from "../lib/api";
import { money, ram } from "../lib/format";

export function CreateServer() {
  const [params] = useSearchParams();
  const preGame = params.get("game") ?? "";
  const prePlan = params.get("plan") ?? "";

  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [gameSlug, setGameSlug] = useState(preGame);
  const [planSlug, setPlanSlug] = useState(prePlan);
  const [regionSlug, setRegionSlug] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<Catalog>("/catalog").then((c) => {
      setCatalog(c);
      const firstGame = c.games[0];
      const firstRegion = c.regions[0];
      if (!gameSlug && firstGame) setGameSlug(firstGame.slug);
      if (firstRegion) setRegionSlug((r) => r || firstRegion.slug);
    });
  }, []);

  const game = useMemo(() => catalog?.games.find((g) => g.slug === gameSlug), [catalog, gameSlug]);
  const plan = useMemo(() => game?.plans.find((p) => p.slug === planSlug), [game, planSlug]);

  useEffect(() => {
    if (game && !game.plans.some((p) => p.slug === planSlug)) {
      setPlanSlug(game.plans[0]?.slug ?? "");
    }
  }, [game]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { server } = await api.post<{ server: ServerView }>("/servers", {
        gameSlug,
        planSlug,
        regionSlug,
        name,
      });
      // Kick off checkout. In fake mode this returns our hosted test page;
      // in production it's a Stripe Checkout URL.
      const { url } = await api.post<{ url: string }>(`/servers/${server.id}/checkout`);
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create server");
      setBusy(false);
    }
  }

  if (!catalog) return <div className="spinner">Loading…</div>;

  return (
    <div className="page">
      <h1>Deploy a server</h1>
      <p className="muted">Compute is only provisioned after checkout completes.</p>

      <div className="grid grid-2 mt">
        <form className="card stack" onSubmit={submit} data-testid="create-form">
          {error && <div className="error-box" data-testid="create-error">{error}</div>}
          <div className="field">
            <label>Server name</label>
            <input className="input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="My survival world" data-testid="server-name" />
          </div>
          <div className="field">
            <label>Game</label>
            <select className="input" value={gameSlug} onChange={(e) => setGameSlug(e.target.value)} data-testid="select-game">
              {catalog.games.map((g) => (
                <option key={g.slug} value={g.slug}>{g.name}</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Plan</label>
            <select className="input" value={planSlug} onChange={(e) => setPlanSlug(e.target.value)} data-testid="select-plan">
              {game?.plans.map((p) => (
                <option key={p.slug} value={p.slug}>{p.name} — {money(p.priceCents)}/mo</option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Region</label>
            <select className="input" value={regionSlug} onChange={(e) => setRegionSlug(e.target.value)} data-testid="select-region">
              {catalog.regions.map((r) => (
                <option key={r.slug} value={r.slug}>{r.name}</option>
              ))}
            </select>
          </div>
          <button className="btn btn-primary" disabled={busy || !plan} data-testid="checkout">
            {busy ? "Redirecting…" : "Continue to checkout"}
          </button>
        </form>

        <div className="card">
          <h3>Order summary</h3>
          {plan ? (
            <>
              <div className="price mt">{money(plan.priceCents)}<small> /mo</small></div>
              <ul className="specs">
                <li>{game?.name}</li>
                <li>{plan.name}</li>
                <li>{ram(plan.ramMb)} RAM · {plan.diskGb} GB disk</li>
                <li>{plan.recommendedPlayers}</li>
                <li>{plan.backupSlots} backup slot{plan.backupSlots > 1 ? "s" : ""}</li>
              </ul>
              <p className="muted">Cancel anytime. Backups retained 7 days after cancellation.</p>
            </>
          ) : (
            <p className="muted">Select a plan to see your summary.</p>
          )}
        </div>
      </div>
    </div>
  );
}
